import { Check, Copy, KeyRound, ShieldCheck } from 'lucide-react'
import QRCode from 'qrcode'
import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { Logo } from '@/components/brand/logo'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { SignInStep } from '@/lib/api/types'
import { useMe, useSignIn, useTwoFactor } from './api'

type Step = { kind: 'password' } | { kind: 'second'; step: SignInStep } | { kind: 'codes'; codes: string[] }

/**
 * Staff sign-in: password, then always a second factor. The first time, the
 * staff member adds Arva SEO to an authenticator app and saves recovery codes.
 */
export function SignInPage() {
  const me = useMe()
  const [step, setStep] = useState<Step>({ kind: 'password' })

  if (me.data && step.kind !== 'codes') return <Navigate to="/" replace />

  return (
    <main className="grid min-h-dvh place-items-center bg-background px-4 py-10">
      <div className="grid w-full max-w-sm gap-6">
        <div className="grid justify-items-center gap-1 text-center">
          <Logo tone="dark" className="mb-3" />
          <p className="eyebrow">Arva SEO staff only</p>
          <h1 className="display text-3xl text-ink">Control Panel</h1>
        </div>
        <section className="grid gap-5 rounded-lg border border-border bg-surface p-6 shadow-card">
          {step.kind === 'password' && <PasswordStep onNext={(s) => setStep({ kind: 'second', step: s })} />}
          {step.kind === 'second' && (
            <SecondStep
              step={step.step}
              onRecoveryCodes={(codes) => setStep({ kind: 'codes', codes })}
              onRestart={() => setStep({ kind: 'password' })}
            />
          )}
          {step.kind === 'codes' && <RecoveryCodes codes={step.codes} />}
        </section>
        <p className="text-center text-xs text-ink-subtle">Every action in the control panel is recorded.</p>
      </div>
    </main>
  )
}

function PasswordStep({ onNext }: { onNext: (step: SignInStep) => void }) {
  const signIn = useSignIn()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        signIn.mutate({ email: email.trim(), password }, { onSuccess: onNext })
      }}
    >
      <Field label="Work email" htmlFor="email" required>
        <Input
          id="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <Field label="Password" htmlFor="password" required>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      {signIn.isError && (
        <p role="alert" className="text-[13px] text-danger">
          {errorMessage(signIn.error)}
        </p>
      )}
      <Button type="submit" loading={signIn.isPending}>
        Continue
      </Button>
    </form>
  )
}

function SecondStep({
  step,
  onRecoveryCodes,
  onRestart,
}: {
  step: SignInStep
  onRecoveryCodes: (codes: string[]) => void
  onRestart: () => void
}) {
  const navigate = useNavigate()
  const verify = useTwoFactor()
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [qr, setQr] = useState<string | null>(null)
  const setup = step.two_factor === 'setup' ? step : null

  useEffect(() => {
    if (setup) void QRCode.toDataURL(setup.otpauth_url, { margin: 1, width: 200 }).then(setQr)
  }, [setup])

  const expired = isApiError(verify.error) && verify.error.code === 'sign_in_expired'

  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        verify.mutate(useRecovery ? { recovery_code: code } : { code }, {
          onSuccess: (data) =>
            data.recovery_codes ? onRecoveryCodes(data.recovery_codes) : void navigate('/'),
        })
      }}
    >
      {setup ? (
        <div className="grid gap-3 text-[13px] text-ink-muted">
          <p className="flex items-center gap-2 font-semibold text-ink">
            <ShieldCheck className="size-4 text-success" aria-hidden="true" /> Set up two-step sign-in
          </p>
          <p>
            Scan this with an authenticator app (Google Authenticator, 1Password, Authy), then enter the
            6-digit code it shows.
          </p>
          {qr && (
            <img
              src={qr}
              alt="QR code for your authenticator app"
              className="mx-auto size-48 rounded-md bg-white p-2"
            />
          )}
          <p className="text-xs">
            Can&apos;t scan? Enter this key:{' '}
            <code className="font-mono break-all text-ink">{setup.secret}</code>
          </p>
        </div>
      ) : (
        <p className="flex items-center gap-2 text-[13px] font-semibold text-ink">
          <KeyRound className="size-4 text-ink-subtle" aria-hidden="true" />
          {useRecovery ? 'Enter one of your recovery codes' : 'Enter the code from your authenticator app'}
        </p>
      )}
      <Field label={useRecovery ? 'Recovery code' : '6-digit code'} htmlFor="code" required>
        <Input
          id="code"
          autoComplete="one-time-code"
          inputMode={useRecovery ? 'text' : 'numeric'}
          required
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="font-mono tracking-widest"
        />
      </Field>
      {verify.isError && (
        <p role="alert" className="text-[13px] text-danger">
          {errorMessage(verify.error)}
        </p>
      )}
      <Button type="submit" loading={verify.isPending}>
        {setup ? 'Turn on and sign in' : 'Sign in'}
      </Button>
      <div className="flex flex-wrap justify-between gap-2 text-xs">
        {!setup && (
          <button
            type="button"
            className="cursor-pointer text-ink-muted underline-offset-2 hover:underline"
            onClick={() => {
              setUseRecovery((v) => !v)
              setCode('')
            }}
          >
            {useRecovery ? 'Use the authenticator app' : 'Use a recovery code'}
          </button>
        )}
        {expired && (
          <button
            type="button"
            className="cursor-pointer text-ink-muted underline-offset-2 hover:underline"
            onClick={onRestart}
          >
            Start again
          </button>
        )}
      </div>
    </form>
  )
}

function RecoveryCodes({ codes }: { codes: string[] }) {
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)

  return (
    <div className="grid gap-4 text-[13px] text-ink-muted">
      <p className="font-semibold text-ink">Save your recovery codes</p>
      <p>
        If you lose your phone, each of these signs you in once. Keep them in your password manager. They
        won&apos;t be shown again.
      </p>
      <ul
        className="grid grid-cols-2 gap-1 rounded-md bg-surface-sunken p-3 font-mono text-ink"
        aria-label="Recovery codes"
      >
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <Button
        variant="secondary"
        onClick={() =>
          void navigator.clipboard.writeText(codes.join('\n')).then(() => {
            setCopied(true)
          })
        }
      >
        {copied ? <Check /> : <Copy />} {copied ? 'Copied' : 'Copy codes'}
      </Button>
      <Button onClick={() => void navigate('/')}>I&apos;ve saved them</Button>
    </div>
  )
}
